/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo } from "react";
import NiceModal from "@ebay/nice-modal-react";
import { ProjectOrFolderDownloadModal } from "components/_Modals/ProjectDownloadModal/ProjectOrFolderDownloadModal";
import { containsConfig, getScrollToIndex } from "utils";
import {
  useCopiedItem,
  useIsHoveringLeftControls,
  useSetIsHoveringLeftControls,
} from "stores/project";
import { useProject, useProjectUIActions } from "stores/project";
import * as identify from "utils/treeUtils";
import { EditNodeTypes } from "variables";
import { useDeleteConfirmationCheck } from "hooks/projectTreeHooks";
import { NodeKinds } from "interfaces/enums";
import { toast } from "react-toastify";
import { DuplicateWizardInProjectMut } from "api/mutations";
import { fetchData } from "utils";
import { WizardLink } from "../WizardLink";
import { NodeTitle } from "../NodeTitle";
import { PasteButton } from "../Buttons/PasteButton";
import { LeftControls, RightControls } from "./FolderControls";
import { useLanguage } from "stores/globalStore";
import { useProjectActions } from "stores/project";
import type { AnyFolder, Node, Root } from "interfaces/schemas";
import type { defaultOptions } from "variables/nodeDefaultOptions";
import { type useProjectTreeContentTranslations } from "hooks/projectTreeHooks";
import type { NodeApi } from "@hogartrabe/react-arborist-vrh";
import { openNodeDownloadModal } from "stores/project/project/actions/openNodeDownloadModal";

type FolderProps = {
  node: NodeApi<Node>;
  options: typeof defaultOptions;
  translations: ReturnType<typeof useProjectTreeContentTranslations>;
  setScrollToIndex: (index: number) => void;
  willReceiveDrop: boolean;
  handleToggleEditMode: (type?: boolean | string) => void;
  isEditMode: boolean | string;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean | string>>;
  handleToggleNodeActiveStatus: () => void;
};

export const Folder = ({
  node,
  options,
  translations,
  setScrollToIndex,
  willReceiveDrop,
  handleToggleEditMode,
  setIsEditMode,
  isEditMode,
  handleToggleNodeActiveStatus,
}: FolderProps) => {
  const actions = useProjectActions();
  const deleteConfirmationCheck = useDeleteConfirmationCheck();
  const copiedItem = useCopiedItem();
  const isHoveringLeftControls = useIsHoveringLeftControls();
  const setIsHoveringLeftControls = useSetIsHoveringLeftControls();
  const { toggleNode, setRedrawIndex } = useProjectUIActions();
  const project = useProject();
  const lang = useLanguage();
  // Dependancies
  const isRootNode = node.data.kind === NodeKinds.ROOT;
  const { folderName, id } = node.data as AnyFolder | Root;
  const isWizard = node.data.kind.startsWith("WIZARD");
  const isWizardRoot = node.data.kind === NodeKinds.WIZARD_ROOT_FOLDER;
  const nodeId = node.id;
  const nodeChildren = node.children;
  const isPasteMode = !!copiedItem;
  const isNodeOpen = node.isOpen;
  const hasChildren = nodeChildren && nodeChildren.length > 0;
  const isNodeOptimistic = nodeId.startsWith("temp__");
  const { rowIndex } = node;

  const applyRedraw =
    (fn = () => {}) =>
    () => {
      if (rowIndex !== null) {
        setRedrawIndex(rowIndex);
      }
      fn();
    };

  useEffect(() => {
    if (willReceiveDrop && rowIndex !== null) {
      setRedrawIndex(rowIndex);
    }
  }, [willReceiveDrop]);

  const handleCopyNode = () => actions.tree.copyNode(node);

  const handleDeleteNode = applyRedraw(() => {
    deleteConfirmationCheck(node, () => actions.tree.deleteNode(node));
  });
  const handleCreateNode = applyRedraw(() => {
    const index = getScrollToIndex(node);
    actions.tree.createNode(node);
    setScrollToIndex(index);
  });
  const onToggleNode = useCallback(() => {
    node.toggle();
    toggleNode(node.data.id);
  }, [node, toggleNode]);
  const handleUpdateNode = (newTitle: string) =>
    actions.tree.updateNode(node, { folderName: newTitle });
  const handleDuplicateNode = applyRedraw(() => {
    if (isWizard) {
      fetchData({
        query: DuplicateWizardInProjectMut,
        variables: {
          input: {
            projectId: project?.id as string,
            nodeId: node.id,
            languageCode: lang,
          },
        },
        onError: (error) => {
          toast.error(error.error.message);
        },
        onSuccess: (data) => {
          if (data.replicateWizard?.status === "SUCCESS") {
            actions.refetch();
          }
          return false;
        },
      });
      return;
    }
    const index = getScrollToIndex(node.parent!);
    actions.tree.duplicateNode(node);
    setScrollToIndex(index);
  });

  const openDownloadModal = useCallback(() => {
    if (isWizardRoot) {
      NiceModal.show(ProjectOrFolderDownloadModal, {
        modalSubtitle: folderName,
        wizardId: nodeId,
        projectId: project?.id,
      });
      return;
    }
    if (!isWizard) {
      openNodeDownloadModal(node, project!);
    }
  }, [id, folderName]);

  const hasConfig = useMemo(() => containsConfig(node), [node]);

  return (
    <>
      <div className="d-flex flex-row align-items-center h-100 overflow-hidden">
        <div
          className="d-flex align-items-center h-100"
          onMouseEnter={() => setIsHoveringLeftControls(true)}
          onMouseLeave={() => setIsHoveringLeftControls(false)}
        >
          <LeftControls
            data={node.data}
            handleToggleNode={onToggleNode}
            handleCreateNode={handleCreateNode}
            isNodeOpen={isNodeOpen}
            hasChildren={!!hasChildren}
            options={options}
            translations={translations}
            nodeId={node.data.id}
          />
        </div>
        <NodeTitle
          title={folderName}
          isEditMode={isEditMode === EditNodeTypes.TITLE}
          setIsEditMode={setIsEditMode}
          handleToggleEditMode={handleToggleEditMode}
          isEditable={!isWizard && !isRootNode && !options.editNodeDisabled}
          handleUpdateTitle={handleUpdateNode}
          className="ms-2 my-1"
          nodeId={nodeId}
        />
        {isWizardRoot && (
          <WizardLink
            node={node}
            params={{ projectId: project?.id ?? "", nodeId: node.data.id }}
            text={translations.folder.wizardLink}
            disabled={isPasteMode}
            handleToggleNodeActiveStatus={handleToggleNodeActiveStatus}
          />
        )}
        {!isWizard &&
          !identify.isTroxNetcom(node.data) &&
          !isHoveringLeftControls && (
            <PasteButton
              text={translations.folder.pasteNode}
              nodeId={id}
              isPasteMode={isPasteMode}
            />
          )}
      </div>
      {!isRootNode && (
        <RightControls
          data={node.data}
          handleToggleEditMode={handleToggleEditMode}
          handleCopyNode={handleCopyNode}
          handleDeleteNode={handleDeleteNode}
          handleDuplicateNode={handleDuplicateNode}
          isPasteMode={isPasteMode}
          options={options}
          isNodeOptimistic={isNodeOptimistic}
          translations={translations}
          nodeId={node.data.id}
          openDownloadModal={openDownloadModal}
          hasConfig={hasConfig}
        />
      )}
    </>
  );
};
